/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Observable, of } from 'rxjs';
import { MyInjector } from '@src/app/app.module';
import { Txn } from '@src/app/model/txn';
import { IFormAction, ActionColor } from '@src/app/shared/form/form.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { PurchaseService } from '@src/app/modules/txn/purchase.service';
import { HttpParams } from '@angular/common/http';
import { concatMap } from 'rxjs/operators';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { GridRow } from '@src/app/shared/grid/grid-row';
import { NrHelper } from '@src/app/shared/NrHelper';

export class AllocateCreditNoteAction implements IFormAction {
    name = $localize`Allocate Credit To Invoices`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'folder_match';
    approvalNeeded = false;
    approvalText = '';
    txn: Txn;
    disabled = true;

    leftToAllocate = 0;

    purchaseSvc = MyInjector.instance.get(PurchaseService);

    checkAllocation = (o: Txn): ValidatorFn => (ctl: AbstractControl): ValidationErrors => {
        if (ctl.value > o.outstanding) {
            return {overPay: $localize `Overallocated`}
        }
        return null;
    }

    removeError(ctl: AbstractControl, errToRemove: string): void {
        const currentErrors: ValidationErrors | null = ctl.errors;
        if (currentErrors && currentErrors[errToRemove]) {
            const newErrors: ValidationErrors = { ...currentErrors };
            delete newErrors[errToRemove];
            if (Object.keys(newErrors).length === 0) {
                ctl.setErrors(null);
            } else {
                ctl.setErrors(newErrors);
            }
        }
    }

    addError(ctl: AbstractControl, errorToAdd: string, msg: string): void {

        // Get current errors, if any
        const currentErrors: ValidationErrors = ctl.errors;

        if (currentErrors && !currentErrors[errorToAdd]) {
            // Create a new object to avoid direct modification of the existing errors object
            const newErrors: ValidationErrors = { ...currentErrors };
            newErrors[errorToAdd] = msg;
            ctl.setErrors(newErrors);
        } else if (!currentErrors) {
            const newErrors = {};
            newErrors[errorToAdd] = msg;
            ctl.setErrors(newErrors);
            ctl.markAsTouched();
        }
    }

    checkTotals (ctl: AbstractControl): ValidationErrors {
        const gridControl = (ctl as GridControl);
        let total = 0;

        gridControl.controls?.forEach((gridRow: GridRow) => {
            const db = gridRow.get('debit');
            if (db) {
                total += db.value;
                this.removeError(db, 'tooMuch');
            }
        });
        this.leftToAllocate = Math.abs(this.txn.outstanding) - total;
        if (total > Math.abs(this.txn.outstanding)) {
            const t = NrHelper.toFormattedCurrency(total);
            const o = NrHelper.toFormattedCurrency(0 - this.txn.outstanding);
            const msg = $localize `Over allocated ${t} for credit of ${o}}`;
            gridControl.controls.forEach((gridRow: GridRow) => {
                const db = gridRow.get('debit');
                if (db.value) {
                    this.addError(db, 'tooMuch', msg);
                }
            });

            return {tooMuch: msg};
        }
        return null;
    }


    getAllocationGrid() {
        return new GridField({
            field:
                { label: 'Allocations', value: 'relations'},
            rowFactory: (o: Txn) => {
                const amtField = FormNumberComponent.makeCurrency('Allocate','debit', {validators:[this.checkAllocation(o)]});
                amtField.numberOpts.width = 6;

                const payer = () => {
                    const avail = Math.min(o?.outstanding, this.leftToAllocate);
                    amtField.control.setValue(avail, {emitEvent: true});
                    amtField.control.updateValueAndValidity()
                };

                const payBtn = FormButtonComponent.makeIconButton('allocateIt', 'check_circle', $localize `Allocate to this invoice`, payer);
                payBtn.cellOpts.style = 'padding-left: 0px; padding-right: 0px';

                const clearer = () => amtField.control.setValue(0);
                const clearBtn = FormButtonComponent.makeIconButton('clearIt', 'cancel', $localize `Clear allocation`, clearer);
                clearBtn.cellOpts.style = 'padding-left: 0px; padding-right: 0px';
                clearBtn.btnOpts.color = 'warn';

                return [
                    FieldMaker.idHolder('id'),
                    FormTextComponent.make('Ref', 'reference', {readonly: true}),
                    FormDateComponent.make('Date', 'txnDate', {readonly: true}),
                    FormNumberComponent.makeCurrency('Total','credit', {readonly: true}),
                    FormNumberComponent.makeCurrency('Outstanding','outstanding', {readonly: true}),
                    amtField,
                    payBtn,
                    clearBtn
            ]}, gridValidator: [this.checkTotals.bind(this)]
        });
    }

    saveAllocations(value: Txn) {
        return this.purchaseSvc.cnAllocate(this.txn, value.relations.filter( o => o.debit > 0));
    }

    openDialog(relations: Txn[]) {
        const dialog = MyInjector.instance.get(MatDialog);
        const fieldSet = new FieldSet({
            fields: [
                this.getAllocationGrid()
            ],
            formLayout: LAYOUT_OPTIONS.simpleDialog
        })
        fieldSet.setValue({relations} as Txn)
        const data = {
            fieldSet,
            saveFn: this.saveAllocations.bind(this),
            saveText: $localize `Save Allocations`,
            title: $localize `Allocate Credit Note ${this.txn.reference} for ${this.txn.supplier.name}`,
            minWidth: '700px',
        } as FieldSetDialogOptions

        return dialog.open(FieldSetDialogComponent, {data} ).afterClosed();
    }

    action(_txn: Txn, _config: FormConfig): Observable<Txn> {
        const cds = MyInjector.instance.get(ConfirmDialogService);
        const retTxn = this.txn;
        const parms = new HttpParams().set('supplierId',retTxn.supplierId).set('typeId', Txn.TYPE.PURCHASE.id).set('outstanding', '>=0.01');
        return this.purchaseSvc.get<Txn>(false, parms).pipe(concatMap( (invoices => {
            if (invoices.length === 0) {
                if (_config) {
                    // If called as an action, rather than called directly from purchaseService
                    const title = $localize`No Matching Invoices`;
                    const msg = $localize`There are no outstanding invoices for this supplier to match this credit note against`;
                    cds.alert(title, msg);
                }
                return of(retTxn);
            }
            return this.openDialog(invoices);
        })));
    }

    setup(txn: Txn) {
        this.txn = txn;
        this.show = false;
        this.disabled = true;
        if (this.txn.typeId === Txn.TYPE.PURCHASE_CN.id) {
            this.show = true;
            if (this.txn.outstanding !== 0) {
                this.disabled = false;
            }
        }
    }
}
